/**
 * Buyer-Demand Phase 2 — admin qualification picker.
 *
 * Modal: search + category chips + scrollable attribute list. Click an
 * attribute to switch to the rule editor for its inputType. Save returns
 * an `AttrQualification` to the parent.
 *
 * Phase 2 ships rule editors for choice / multi_select / range. Other
 * input types (text, boolean, date) fall back to a degenerate choice
 * selection over their canonical options where possible.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, ChevronLeft } from 'lucide-react';
import {
  fetchCanonicalAttributes,
  type AttrQualification,
  type CanonicalAttributeView,
  type AttributeCategory,
} from '../lib/adminApi';

const CATEGORY_LABELS: Record<AttributeCategory, string> = {
  demographics: 'Demographics',
  geography: 'Geography',
  household: 'Household',
  employment: 'Employment',
  education: 'Education',
  health: 'Health',
  auto: 'Auto',
  consumer_behavior: 'Consumer behaviour',
  political: 'Political',
  lifestyle: 'Lifestyle',
  b2b: 'B2B',
  custom: 'Custom',
};

const CATEGORY_ORDER: AttributeCategory[] = [
  'demographics', 'geography', 'household', 'employment', 'education',
  'health', 'auto', 'consumer_behavior', 'political', 'lifestyle', 'b2b',
];

interface Props {
  onSave: (qual: AttrQualification) => void;
  onClose: () => void;
}

export default function QualificationPicker({ onSave, onClose }: Props) {
  const [selected, setSelected] = useState<CanonicalAttributeView | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<AttributeCategory | 'all'>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'canonical-attributes'],
    queryFn: fetchCanonicalAttributes,
  });

  const filtered = useMemo(() => {
    const all = data?.attributes ?? [];
    let list = all.filter((a) => !a.deprecated);
    if (categoryFilter !== 'all') list = list.filter((a) => a.category === categoryFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((a) =>
        a.label.toLowerCase().includes(q) || a.key.toLowerCase().includes(q));
    }
    return list;
  }, [data, search, categoryFilter]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-slate-700">
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            {selected && (
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-white"
                aria-label="Back to list"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-lg font-bold text-white">
              {selected ? selected.label : 'Add qualification'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {!selected ? (
          <>
            <div className="px-6 py-3 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search attributes…"
                  className="w-full pl-9 bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
                />
              </div>
              <div className="flex gap-1 mt-3 flex-wrap">
                <CategoryChip active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')}>
                  All
                </CategoryChip>
                {CATEGORY_ORDER.map((cat) => (
                  <CategoryChip
                    key={cat}
                    active={categoryFilter === cat}
                    onClick={() => setCategoryFilter(cat)}
                  >
                    {CATEGORY_LABELS[cat]}
                  </CategoryChip>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-auto p-2">
              {isLoading ? (
                <p className="text-sm text-slate-400 p-4">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-slate-400 p-4">No matches.</p>
              ) : (
                filtered.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => setSelected(a)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-800 rounded-lg flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm text-white font-medium">{a.label}</p>
                      <p className="text-xs text-slate-500">
                        {CATEGORY_LABELS[a.category]} · {a.inputType}
                        {a.b2b ? ' · B2B' : ''}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">
                      {a.memberCount.toLocaleString()} members
                    </span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <RuleEditor
            attribute={selected}
            onSave={(rule) => onSave({ attributeKey: selected.key, match: rule })}
          />
        )}
      </div>
    </div>
  );
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1 rounded-full transition-colors ${
        active ? 'bg-primary text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function RuleEditor({
  attribute,
  onSave,
}: {
  attribute: CanonicalAttributeView;
  onSave: (rule: import('../lib/adminApi').ManualMatchRule) => void;
}) {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [requireAny, setRequireAny] = useState(true);
  const [rangeMin, setRangeMin] = useState<string>('');
  const [rangeMax, setRangeMax] = useState<string>('');

  // Phase 2.5 — date_of_birth gets a dedicated age-range editor that
  // emits the new `age_range` match kind. Generic `range` over a date
  // string would never match (the matcher would parseFloat the ISO
  // date and the qual would silently exclude every member).
  const isAgeRange = attribute.key === 'date_of_birth';
  const supportsChoice = attribute.inputType === 'single_select' && (attribute.options?.length ?? 0) > 0;
  const supportsMulti = attribute.inputType === 'multi_select' && (attribute.options?.length ?? 0) > 0;
  const supportsRange = !isAgeRange && (attribute.inputType === 'number' || attribute.inputType === 'date');
  const supportsBoolean = attribute.inputType === 'boolean';

  // Sensible default for age range — most buyer briefs target 18+,
  // and 65 is a natural upper end. Admin overrides as needed.
  const [minAge, setMinAge] = useState<string>(isAgeRange ? '18' : '');
  const [maxAge, setMaxAge] = useState<string>(isAgeRange ? '65' : '');

  function toggleValue(v: string) {
    setSelectedValues((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  }

  function handleSave() {
    if (isAgeRange) {
      const minA = parseInt(minAge, 10);
      const maxA = parseInt(maxAge, 10);
      onSave({ kind: 'age_range', minAge: minA, maxAge: maxA });
      return;
    }
    if (supportsChoice || supportsBoolean) {
      onSave({ kind: 'choice', values: selectedValues });
    } else if (supportsMulti) {
      onSave({ kind: 'multi_select', values: selectedValues, require_any: requireAny });
    } else if (supportsRange) {
      const min = rangeMin ? Number(rangeMin) : undefined;
      const max = rangeMax ? Number(rangeMax) : undefined;
      onSave({ kind: 'range', min, max });
    }
  }

  const ageRangeValid =
    isAgeRange &&
    minAge !== '' && maxAge !== '' &&
    !Number.isNaN(parseInt(minAge, 10)) && !Number.isNaN(parseInt(maxAge, 10)) &&
    parseInt(minAge, 10) <= parseInt(maxAge, 10);

  const canSave =
    (isAgeRange && ageRangeValid) ||
    (!isAgeRange && supportsChoice && selectedValues.length > 0) ||
    (!isAgeRange && supportsBoolean && selectedValues.length > 0) ||
    (!isAgeRange && supportsMulti && selectedValues.length > 0) ||
    (!isAgeRange && supportsRange && (rangeMin !== '' || rangeMax !== ''));

  return (
    <div className="flex-1 overflow-auto p-6 space-y-4">
      {isAgeRange && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-400">Age range (years)</p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={13}
              max={120}
              placeholder="Min age"
              value={minAge}
              onChange={(e) => setMinAge(e.target.value)}
              className="bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 w-32"
            />
            <span className="text-slate-500 text-sm">to</span>
            <input
              type="number"
              min={13}
              max={120}
              placeholder="Max age"
              value={maxAge}
              onChange={(e) => setMaxAge(e.target.value)}
              className="bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 w-32"
            />
          </div>
          <p className="text-xs text-slate-500">
            Both bounds inclusive. Members with no DOB on file fail this qual
            (no prescreen — DOB is captured at signup).
          </p>
        </div>
      )}

      {!isAgeRange && (supportsChoice || supportsMulti) && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-400">
            {supportsMulti ? 'Select one or more values' : 'Select accepted values'}
          </p>
          <div className="space-y-1">
            {(attribute.options ?? []).map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 px-3 py-2 hover:bg-slate-800 rounded-lg cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt.value)}
                  onChange={() => toggleValue(opt.value)}
                />
                <span className="text-sm text-white">{opt.label}</span>
              </label>
            ))}
          </div>
          {supportsMulti && (
            <label className="flex items-center gap-2 mt-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={requireAny}
                onChange={(e) => setRequireAny(e.target.checked)}
              />
              Member must match at least one (uncheck for "must match all")
            </label>
          )}
        </div>
      )}

      {!isAgeRange && supportsBoolean && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-400">Required value</p>
          <div className="flex gap-2">
            {['yes', 'no'].map((v) => (
              <button
                key={v}
                onClick={() => setSelectedValues([v])}
                className={`px-4 py-2 rounded-lg text-sm ${
                  selectedValues.includes(v)
                    ? 'bg-primary text-white'
                    : 'bg-slate-800 text-slate-300'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      )}

      {!isAgeRange && supportsRange && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-400">Range</p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="min"
              value={rangeMin}
              onChange={(e) => setRangeMin(e.target.value)}
              className="bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 w-32"
            />
            <span className="text-slate-500 text-sm">to</span>
            <input
              type="number"
              placeholder="max"
              value={rangeMax}
              onChange={(e) => setRangeMax(e.target.value)}
              className="bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 w-32"
            />
          </div>
        </div>
      )}

      {!isAgeRange && !supportsChoice && !supportsMulti && !supportsBoolean && !supportsRange && (
        <p className="text-sm text-amber-400">
          This attribute's input type ({attribute.inputType}) does not have a
          rule editor in Phase 2. Add via custom question or wait for the
          full editor in a later phase.
        </p>
      )}

      <div className="pt-4 flex justify-end gap-2 border-t border-slate-700">
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          Add to project
        </button>
      </div>
    </div>
  );
}

// Preserved no-op re-export so the type is reachable at runtime by the
// surrounding admin form modules without circular imports.
export type { AttrQualification };

// `useEffect` was used during scaffolding but is no longer needed —
// the `useQuery` hook handles data fetching. Kept as an explicit
// import-side-effect-free reference until the form integration lands.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _useEffectKeepImport = useEffect;
